import { MailIcon, PhoneIcon } from '@heroicons/react/outline'
import { c, FC } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { useIntl } from 'lib/intl-utils'
import { useEffect, useState } from 'react'
import { TelegramIcon, WhatsAppIcon, YouTubeIcon } from 'components/icons'
import { useRouter } from 'next/router'
import Link from 'components/base/Link'
import { useTheme } from 'lib/colors'

const links = [
  {
    name: 'Telegram',
    prefix: 'https://t.me/',
    href: (c: any) => c.telegram,
    icon: TelegramIcon,
  },
  {
    name: 'WhatsApp',
    prefix: 'https://wa.me/',
    href: (c: any) => c.whatsapp,
    icon: WhatsAppIcon,
  },
  {
    name: 'Email',
    prefix: 'mailto:',
    href: (c: any) => c.mail,
    icon: MailIcon,
  },
  {
    name: 'Phone',
    prefix: 'tel:',
    href: (c: any) => c.phone,
    icon: PhoneIcon,
  },
  {
    name: 'YouTube',
    prefix: 'https://youtube.com/',
    href: (c: any) => c.youtube,
    icon: YouTubeIcon,
  },
]

const SellematicsFooter: FC = ({ className }) => {
  const { t, lang } = useIntl()
  const { theme, isAuthorized } = useUserContext()
  const router = useRouter()
  const [hide, setHide] = useState(false)
  const { isDark } = useTheme()

  useEffect(() => {
    const iframe = document.querySelector('main > div > iframe')

    if (iframe !== null) {
      setHide(true)
    } else {
      setHide(false)
    }
  }, [router.asPath])

  const currentYear = new Date().getFullYear()
  const lifespan =
    theme.releaseDate !== currentYear ? theme.releaseDate + '–' + currentYear : theme.releaseDate

  if (hide) return null

  return (
    <footer className="bg-white border-t border-gray-200">
      <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:py-16 lg:px-8">
        <div className="lg:flex lg:gap-8">
          <div className="space-y-6 xl:col-span-1">
            <img
              src={isDark ? theme.logoWhite : theme.logoDark}
              className={c`w-44`}
              alt={theme.title}
            />
            <div className="text-base text-gray-700">{t`companies.sellematics.description`}</div>
          </div>
          <div className="grid grid-cols-2 grid-rows-2 gap-y-8 mt-12 grow sm:gap-x-16 sm:grid-flow-col sm:grid-cols-none sm:grid-rows-1 lg:justify-items-end xl:col-span-2 lg:mt-0">
            <div>
              <div className="flex flex-col space-y-4 text-base text-gray-700">
                <p className="font-medium">{t`company`}</p>
                <Link href="/articles/" className="hover:text-gray-900">
                  {t`links.blog`}
                </Link>
                <Link href="/about/" className="hover:text-gray-900">
                  {t`links.about`}
                </Link>
                <Link href="/repricer/" className="hover:text-gray-900">
                  {t`links.repricer`}
                </Link>
                <Link
                  href="/marketplace-analytics-mcp/"
                  target="_blank"
                  rel="noreferrer nofollow"
                  className="hover:text-gray-900"
                >
                  {t`links.mcp_analytics`}
                </Link>
                <Link
                  href="/store-analytics-mcp/"
                  target="_blank"
                  rel="noreferrer nofollow"
                  className="hover:text-gray-900"
                >
                  {t`links.mcp_inner_analytics`}
                </Link>
                {isAuthorized && theme.extensionUrl && (
                  <a
                    href={theme.extensionUrl}
                    target="_blank"
                    rel="noreferrer nofollow"
                    className="hover:text-gray-900"
                  >
                    {t`links.extension`}
                  </a>
                )}
              </div>
            </div>
            <div>
              <div className="space-y-4">
                <div>
                  <a href="tel:+74954141011" className="text-xl text-gray-700 hover:text-gray-900">
                    +7 495 414-10-11
                  </a>
                </div>
                <div>
                  <a
                    href="mailto:info@sellematics.com"
                    className="text-base text-gray-700 hover:text-gray-900"
                  >
                    info@sellematics.com
                  </a>
                </div>
              </div>
            </div>
            <div>
              <div className="space-y-3 text-base text-gray-700">
                <div className="font-medium">{t`companies.sellematics.name`}</div>
                <div className="grid grid-rows-2 grid-flow-col gap-y-3 gap-x-8">
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">{t`inn`}</span>
                    <span>7727492799</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">{t`kpp`}</span>
                    <span>237801001</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500">{t`ogrn`}</span>
                    <span>1227700252878</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-baseline pt-8 mt-12 space-y-4 border-t border-gray-200 lg:flex-row">
          <div>
            <p className="text-base text-gray-400">
              {lang === 'en' && (
                <>
                  &copy; {lifespan} {theme.title}.
                </>
              )}
              {lang === 'ru' && (
                <>
                  &copy; {theme.title}, {lifespan}.
                </>
              )}
              {lang === 'uz' && (
                <>
                  &copy; {lifespan} {theme.title}.
                </>
              )}
              {lang === 'zh' && (
                <>
                  &copy; {lifespan} {theme.title}.
                </>
              )}{' '}
              {t`footer.all_rights_reserved`}.
            </p>
          </div>
          <div className="flex flex-col space-y-2 text-gray-400 lg:space-x-6 lg:space-y-0 lg:flex-row lg:ml-auto">
            <a
              className="text-sm hover:text-gray-500"
              href="/doc/sellematics/ru/privacy_policy.pdf"
            >
              {t`footer.privacy_policy`}
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default SellematicsFooter
